/**
 * ValidationSummary - Summary cards component
 * 
 * Displays aggregate validation metrics in card format:
 * - Missing images count
 * - Missing translations (names and descriptions)
 * - Missing prices count
 */

/* eslint-disable react/prop-types */
import React from "react";

const ValidationSummary = ({ summary }) => {
  if (!summary) return null;

  const { issues } = summary;

  return (
    <div className="ls-validation-summary">
      <div className="ls-validation-summary-grid">
        {/* Missing Images Card */}
        <div className="ls-validation-summary-card">
          <div className="ls-validation-summary-card-icon ls-validation-summary-card-icon--error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/>
              <path d="M21 5v14c0 1.1-.9 2-2 2H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="ls-validation-summary-card-content">
            <p className="ls-validation-summary-card-label">Missing Images</p>
            <p className="ls-validation-summary-card-value">
              {issues.missingImages || 0}
            </p>
            <p className="ls-validation-summary-card-unit">items</p>
          </div>
        </div>

        {/* Missing Translations Card */}
        <div className="ls-validation-summary-card">
          <div className="ls-validation-summary-card-icon ls-validation-summary-card-icon--warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="currentColor"/>
            </svg>
          </div>
          <div className="ls-validation-summary-card-content">
            <p className="ls-validation-summary-card-label">Missing Translations</p>
            <div className="ls-validation-summary-card-split">
              <div>
                <p className="ls-validation-summary-card-value">
                  {issues.missingTranslations?.names || 0}
                </p>
                <p className="ls-validation-summary-card-unit">names</p>
              </div>
              <div>
                <p className="ls-validation-summary-card-value">
                  {issues.missingTranslations?.descriptions || 0}
                </p>
                <p className="ls-validation-summary-card-unit">descriptions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Missing Prices Card */}
        <div className="ls-validation-summary-card">
          <div className="ls-validation-summary-card-icon ls-validation-summary-card-icon--info">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" fill="currentColor"/>
            </svg>
          </div>
          <div className="ls-validation-summary-card-content">
            <p className="ls-validation-summary-card-label">Missing Prices</p>
            <p className="ls-validation-summary-card-value">
              {issues.invalidPrices || 0}
            </p>
            <p className="ls-validation-summary-card-unit">items</p>
          </div>
        </div>

        {/* Missing Calorie Info Card */}
        <div className="ls-validation-summary-card">
          <div className="ls-validation-summary-card-icon ls-validation-summary-card-icon--warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.06 22.99h1.66c.84 0 1.53-.64 1.63-1.46L23 5.05h-5V1h-1.97v4.05h-4.97l.3 2.34c1.71.47 3.31 1.32 4.27 2.26 1.44 1.42 2.43 2.89 2.43 5.29v8.05zM1 21.99V21h15.03v.99c0 .55-.45 1-1.01 1H2.01c-.56 0-1.01-.45-1.01-1zm15.03-7c0-2.29-1.96-4.22-4.41-5.22A12.47 12.47 0 007 9c-1.56 0-3.05.32-4.33.81C.27 10.84 1 12.86 1 15v4h15.03v-4z" fill="currentColor"/>
            </svg>
          </div>
          <div className="ls-validation-summary-card-content">
            <p className="ls-validation-summary-card-label">Missing Calorie Info</p>
            <p className="ls-validation-summary-card-value">
              {issues.missingEnergyInfo || 0}
            </p>
            <p className="ls-validation-summary-card-unit">items</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationSummary;

